import { useState, useMemo, useRef, useEffect } from 'react';
import { usePagination } from '../hooks/usePagination';
import Pagination from '../components/ui/Pagination';
import { createPortal } from 'react-dom';
import { PDFLink } from "../components/InvoicePDF";
import EmailModal from "../components/modals/EmailModal";
import MultiSelectDropdown from "../components/ui/MultiSelectDropdown";
import DateRangePicker from "../components/ui/DateRangePicker";
import { ChevronDown } from 'lucide-react';

// Actions Dropdown Component
const ActionsDropdown = ({ invoice, clients, settings, openModal, handleEmailInvoice, onRecordPayment, onViewPayments, onProcessPayment, deleteItem, canRecordPayment, paymentStatus }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef(null);

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.right - 192 + window.scrollX // 192px = w-48 width
      });
    }
  }, [isOpen]);

  return (
    <div className="relative inline-block text-left">
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className="inline-flex items-center px-3 py-1 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        type="button"
      >
        Actions
        <ChevronDown size={16} className="ml-1" />
      </button>
      
      {isOpen && createPortal(
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setIsOpen(false)}
          ></div>
          
          {/* Dropdown Menu */}
          <div 
            className="absolute z-50 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none"
            style={{
              top: dropdownPosition.top,
              left: dropdownPosition.left
            }}
          >
            <div className="py-1">
              <button
                className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                onClick={() => {
                  openModal('invoice', invoice);
                  setIsOpen(false);
                }}
              >
                <i className="fas fa-edit mr-3"></i>
                Edit
              </button>
              
              <PDFLink 
                item={invoice} 
                type="invoice" 
                clients={clients} 
                companySettings={settings}
                className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                onClick={() => setIsOpen(false)}
              />
              
              {/* Email Section */}
              <div className="border-t border-gray-100"></div>
              <button
                className={`flex items-center w-full px-4 py-2 text-sm ${
                  !settings?.smtpHost 
                    ? 'text-gray-400 cursor-not-allowed' 
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
                onClick={() => {
                  if (settings?.smtpHost) {
                    handleEmailInvoice(invoice);
                    setIsOpen(false);
                  }
                }}
                disabled={!settings?.smtpHost}
              >
                <i className="fas fa-envelope mr-3"></i>
                <div className="flex flex-col items-start">
                  <span>Email Invoice</span>
                  {!settings?.smtpHost && (
                    <small className="text-gray-400 text-xs">
                      (Email not configured)
                    </small>
                  )}
                </div>
              </button>
              
              {/* Payment related actions */}
              {(canRecordPayment || (invoice.payments && invoice.payments.length > 0)) && (
                <div className="border-t border-gray-100"></div>
              )}
              {canRecordPayment && (
                <button
                  className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  onClick={() => {
                    onRecordPayment(invoice);
                    setIsOpen(false);
                  }}
                >
                  <i className="fas fa-money-bill mr-3"></i>
                  Record Payment
                </button>
              )}
              {invoice.payments && invoice.payments.length > 0 && (
                <button
                  className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  onClick={() => {
                    onViewPayments(invoice);
                    setIsOpen(false);
                  }}
                >
                  <i className="fas fa-list mr-3"></i>
                  View Payments
                </button>
              )}
              {paymentStatus.remaining > 0 && onProcessPayment && (
                <button
                  className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  onClick={() => {
                    onProcessPayment(invoice);
                    setIsOpen(false);
                  }}
                >
                  <i className="fas fa-credit-card mr-3"></i>
                  Process Payment
                </button>
              )}
              <div className="border-t border-gray-100"></div>
              <button
                className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                onClick={() => {
                  deleteItem(invoice._id, 'invoice');
                  setIsOpen(false);
                }}
              >
                <i className="fas fa-trash mr-3"></i>
                Delete
              </button>
            </div>
          </div>
        </>,
        document.body
      )}
    </div>
  );
};

const InvoicesPage = ({ 
  invoices, 
  clients, 
  openModal, 
  deleteItem, 
  settings, 
  onRecordPayment, 
  onViewPayments,
  onProcessPayment,
  onInvoiceUpdate 
}) => {
  const [emailModal, setEmailModal] = useState({
    isOpen: false,
    invoice: null,
    client: null
  });

  const [filters, setFilters] = useState({
    status: [], // Changed to array for multi-select
    search: '',
    dateRange: { type: 'preset', value: 'all' }
  });

  const formatDate = (date) => new Date(date).toLocaleDateString();
  const formatCurrency = (amount) => `${settings.currency}${parseFloat(amount || 0).toFixed(2)}`;
  
  // Status options for multi-select dropdown
  const statusOptions = [
    { value: 'draft', label: 'Draft' },
    { value: 'sent', label: 'Sent' },
    { value: 'payment-pending', label: 'Payment Processing' },
    { value: 'partially-paid', label: 'Partially Paid' },
    { value: 'paid', label: 'Paid' },
    { value: 'overdue', label: 'Overdue' },
    { value: 'cancelled', label: 'Cancelled' }
  ];
  
  const getStatusBadge = (status) => {
    const badges = {
      draft: 'bg-gray-500',
      sent: 'bg-blue-500',
      paid: 'bg-green-500',
      'partially-paid': 'bg-yellow-500',
      'payment-pending': 'bg-blue-400',
      overdue: 'bg-red-500'
    };
    return `inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium text-white ${badges[status] || 'bg-gray-500'}`;
  };

  const getPaymentStatus = (invoice) => {
    // Use database status directly
    const status = invoice.status;
    const remaining = Math.max(0, (invoice.totalAmount || 0) - (invoice.totalPaid || 0));
    
    const statusLabels = {
      'draft': 'Draft',
      'sent': 'Sent', 
      'paid': 'Paid',
      'partially-paid': 'Partially Paid',
      'overdue': 'Overdue',
      'cancelled': 'Cancelled'
    };
    
    return { 
      status: status, 
      label: statusLabels[status] || status, 
      remaining: remaining 
    };
  };

  const canRecordPayment = (invoice) => {
    const paymentStatus = getPaymentStatus(invoice);
    return invoice.status !== 'draft' && paymentStatus.remaining > 0;
  };

  const handleEmailInvoice = (invoice) => {
    const client = clients.find(c => c._id === invoice.clientId);
    if (!client) {
      alert('Client not found for this invoice');
      return;
    }
    
    setEmailModal({
      isOpen: true,
      invoice: invoice,
      client: client
    });
  };

  const handleEmailSent = (invoice, recipientEmail) => {
    // Update invoice status to 'sent' if it was 'draft'
    if (invoice.status === 'draft' && onInvoiceUpdate) {
      onInvoiceUpdate(invoice._id, { ...invoice, status: 'sent' });
    }
    
    console.log(`Invoice ${invoice.number} emailed to ${recipientEmail}`);
    // You could show a toast notification here
  };

  const closeEmailModal = () => {
    setEmailModal({
      isOpen: false,
      invoice: null,
      client: null
    });
  };

  // Filter invoices based on selected filters
  const filteredInvoices = useMemo(() => {
    return invoices.filter(invoice => {
      const client = clients.find(c => c._id === invoice.clientId);
      
      // Status filter - use database status directly (multi-select)
      if (filters.status.length > 0 && !filters.status.includes(invoice.status)) {
        return false;
      }

      // Search filter
      if (filters.search) {
        const searchTerm = filters.search.toLowerCase();
        const matchesSearch = (
          invoice.number.toLowerCase().includes(searchTerm) ||
          (client?.name || '').toLowerCase().includes(searchTerm) ||
          invoice._id.toString().includes(searchTerm)
        );
        if (!matchesSearch) {
          return false;
        }
      }

      // Date range filter
      if (filters.dateRange.value !== 'all' || filters.dateRange.type === 'custom') {
        const invoiceDate = new Date(invoice.date);
        
        if (filters.dateRange.type === 'custom') {
          const startDate = new Date(filters.dateRange.startDate);
          const endDate = new Date(filters.dateRange.endDate);
          endDate.setHours(23, 59, 59, 999); // Include full end date
          
          if (invoiceDate < startDate || invoiceDate > endDate) {
            return false;
          }
        } else {
          const today = new Date();
          today.setHours(23, 59, 59, 999);
          
          switch (filters.dateRange.value) {
            case 'today':
              const startOfToday = new Date();
              startOfToday.setHours(0, 0, 0, 0);
              if (invoiceDate < startOfToday || invoiceDate > today) return false;
              break;
              
            case 'yesterday':
              const yesterday = new Date();
              yesterday.setDate(yesterday.getDate() - 1);
              const startOfYesterday = new Date(yesterday);
              startOfYesterday.setHours(0, 0, 0, 0);
              const endOfYesterday = new Date(yesterday);
              endOfYesterday.setHours(23, 59, 59, 999);
              if (invoiceDate < startOfYesterday || invoiceDate > endOfYesterday) return false;
              break;
              
            case 'last7days':
              const sevenDaysAgo = new Date();
              sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
              sevenDaysAgo.setHours(0, 0, 0, 0);
              if (invoiceDate < sevenDaysAgo) return false;
              break;
              
            case 'last30days':
              const thirtyDaysAgo = new Date();
              thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
              thirtyDaysAgo.setHours(0, 0, 0, 0);
              if (invoiceDate < thirtyDaysAgo) return false;
              break;
              
            case 'last90days':
              const ninetyDaysAgo = new Date();
              ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);
              ninetyDaysAgo.setHours(0, 0, 0, 0);
              if (invoiceDate < ninetyDaysAgo) return false;
              break;
              
            case 'thisMonth':
              const startOfThisMonth = new Date(today.getFullYear(), today.getMonth(), 1);
              const endOfThisMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
              endOfThisMonth.setHours(23, 59, 59, 999);
              if (invoiceDate < startOfThisMonth || invoiceDate > endOfThisMonth) return false;
              break;
              
            case 'lastMonth':
              const startOfLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
              const endOfLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
              endOfLastMonth.setHours(23, 59, 59, 999);
              if (invoiceDate < startOfLastMonth || invoiceDate > endOfLastMonth) return false;
              break;
              
            case 'thisYear':
              const startOfThisYear = new Date(today.getFullYear(), 0, 1);
              const endOfThisYear = new Date(today.getFullYear(), 11, 31);
              endOfThisYear.setHours(23, 59, 59, 999);
              if (invoiceDate < startOfThisYear || invoiceDate > endOfThisYear) return false;
              break;
              
            case 'lastYear':
              const startOfLastYear = new Date(today.getFullYear() - 1, 0, 1);
              const endOfLastYear = new Date(today.getFullYear() - 1, 11, 31);
              endOfLastYear.setHours(23, 59, 59, 999);
              if (invoiceDate < startOfLastYear || invoiceDate > endOfLastYear) return false;
              break;
              
            default:
              break;
          }
        }
      }

      return true;
    });
  }, [invoices, clients, filters]);

  // Paginate the filtered list. Summary totals below still run over the whole
  // filtered set, not just the visible page.
  const {
    pageItems: pagedInvoices, page, setPage, pageSize, setPageSize,
    totalPages, total: totalFiltered, rangeStart, rangeEnd,
  } = usePagination(filteredInvoices, { initialPageSize: 25, resetKey: JSON.stringify(filters) });


  return (
    <>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-semibold mb-2">Invoices</h2>
          <p className="text-gray-600">Create and manage invoices</p>
        </div>
        <button
          onClick={() => openModal('invoice')}
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <i className="fas fa-plus mr-2"></i>
          Create Invoice
        </button>
      </div>
      
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          
          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <MultiSelectDropdown
                label="Status"
                options={statusOptions}
                selected={filters.status}
                onChange={(newSelected) => setFilters({...filters, status: newSelected})}
                placeholder="All Statuses"
                className="w-full"
              />
            </div>
            
            <div>
              <DateRangePicker
                label="Date Range"
                value={filters.dateRange}
                onChange={(newDateRange) => setFilters({...filters, dateRange: newDateRange})}
                className="w-full"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <i className="fas fa-search text-gray-400"></i>
                </div>
                <input
                  type="text"
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md text-sm placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Invoice #, Client..."
                  value={filters.search}
                  onChange={(e) => setFilters({...filters, search: e.target.value})}
                />
              </div>
            </div>
          </div>
          
          {/* Filter Summary and Results Count */}
          <div className="mt-4 flex justify-between items-center flex-wrap gap-2">
            <div className="flex items-center gap-2 flex-wrap">
              {(filters.status.length > 0 || filters.search || (filters.dateRange.value !== 'all' || filters.dateRange.type === 'custom')) && (
                <>
                  <span className="text-sm text-gray-500">Filters:</span>
                {filters.status.length > 0 && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                    Status: {
                      filters.status.length === 1 
                        ? statusOptions.find(opt => opt.value === filters.status[0])?.label || filters.status[0]
                        : `${filters.status.length} selected`
                    }
                    <button 
                      className="ml-1 inline-flex items-center justify-center w-4 h-4 text-gray-400 hover:text-gray-600"
                      onClick={() => setFilters({...filters, status: []})}
                    >
                      <span className="sr-only">Remove filter</span>
                      ×
                    </button>
                  </span>
                )}
                {filters.search && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                    Search: "{filters.search}"
                    <button 
                      className="ml-1 inline-flex items-center justify-center w-4 h-4 text-gray-400 hover:text-gray-600"
                      onClick={() => setFilters({...filters, search: ''})}
                    >
                      <span className="sr-only">Remove filter</span>
                      ×
                    </button>
                  </span>
                )}
                {(filters.dateRange.value !== 'all' || filters.dateRange.type === 'custom') && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                    Date: {
                      filters.dateRange.type === 'custom' && filters.dateRange.startDate && filters.dateRange.endDate
                        ? `${new Date(filters.dateRange.startDate).toLocaleDateString()} - ${new Date(filters.dateRange.endDate).toLocaleDateString()}`
                        : filters.dateRange.value.replace(/([A-Z])/g, ' $1').toLowerCase()
                    }
                    <button 
                      className="ml-1 inline-flex items-center justify-center w-4 h-4 text-gray-400 hover:text-gray-600"
                      onClick={() => setFilters({...filters, dateRange: { type: 'preset', value: 'all' }})}
                    >
                      <span className="sr-only">Remove filter</span>
                      ×
                    </button>
                  </span>
                )}
                  <button 
                    className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                    onClick={() => setFilters({status: [], search: '', dateRange: { type: 'preset', value: 'all' }})}
                  >
                    Clear all
                  </button>
                </>
              )}
            </div>
            
            <span className="text-sm text-gray-500">
              {filteredInvoices.length} of {invoices.length} invoices
            </span>
          </div>
        </div>
        
        <div className="overflow-hidden">
          {filteredInvoices.length === 0 ? (
            <div className="text-center py-12">
              <i className="fas fa-file-invoice text-4xl text-gray-400 mb-4"></i>
              <h5 className="text-lg font-medium text-gray-900 mb-2">
                {invoices.length === 0 ? 'No invoices yet' : 'No invoices match your filters'}
              </h5>
              <p className="text-gray-500 mb-4">
                {invoices.length === 0 
                  ? 'Create your first invoice to get started'
                  : 'Try adjusting your filters or search criteria'
                }
              </p>
              {invoices.length === 0 && (
                <button
                  onClick={() => openModal('invoice')}
                  className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Create Invoice
                </button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Number</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Client</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Due Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Paid</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Balance</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {pagedInvoices.map(invoice => {
                    const client = clients.find(c => c._id === invoice.clientId);
                    const total = invoice.totalAmount || 0; // Use database total
                    const totalPaid = invoice.totalPaid || 0; // Use database total_paid
                    const paymentStatus = getPaymentStatus(invoice);
                    
                    return (
                      <tr key={invoice._id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <button
                            onClick={() => openModal('invoice', invoice)}
                            className="text-blue-600 hover:text-blue-800 font-medium"
                          >
                            {invoice.number}
                          </button>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {client?.name || (
                            <span className="text-gray-400">Unknown Client</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatDate(invoice.date)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {invoice.dueDate ? (
                            <span className={new Date(invoice.dueDate) < new Date() && paymentStatus.status !== 'paid' ? 'text-red-600' : 'text-gray-900'}>
                              {formatDate(invoice.dueDate)}
                            </span>
                          ) : (
                            <span className="text-gray-400">No due date</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-1">
                            <span className={getStatusBadge(paymentStatus.status)}>
                              {paymentStatus.label}
                            </span>
                            {invoice.isRecurring && (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" title="Recurring Invoice">
                                <i className="fas fa-repeat"></i>
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-right text-gray-900">{formatCurrency(total)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                          {totalPaid > 0 ? (
                            <span className="text-green-600 font-medium">{formatCurrency(totalPaid)}</span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                          {paymentStatus.remaining > 0 ? (
                            <span className={`font-medium ${paymentStatus.status === 'overdue' ? 'text-red-600' : 'text-yellow-600'}`}>
                              {formatCurrency(paymentStatus.remaining)}
                            </span>
                          ) : (
                            <span className="text-green-600">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <ActionsDropdown 
                            invoice={invoice}
                            clients={clients}
                            settings={settings}
                            openModal={openModal}
                            handleEmailInvoice={handleEmailInvoice}
                            onRecordPayment={onRecordPayment}
                            onViewPayments={onViewPayments}
                            onProcessPayment={onProcessPayment}
                            deleteItem={deleteItem}
                            canRecordPayment={canRecordPayment(invoice)}
                            paymentStatus={paymentStatus}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <Pagination
          page={page}
          totalPages={totalPages}
          total={totalFiltered}
          rangeStart={rangeStart}
          rangeEnd={rangeEnd}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          label="invoices"
        />

        {/* Summary Cards */}
        <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            <div>
              <div className="text-xs text-gray-500 mb-1">Total Invoices</div>
              <div className="text-lg font-semibold text-gray-900">{filteredInvoices.length}</div>
            </div>
            <div>
              <div className="text-xs text-gray-500 mb-1">Total Value</div>
              <div className="text-lg font-semibold text-gray-900">
                {formatCurrency(filteredInvoices.reduce((sum, inv) => sum + (inv.totalAmount || 0), 0))}
              </div>
            </div>
            <div>
              <div className="text-xs text-gray-500 mb-1">Total Paid</div>
              <div className="text-lg font-semibold text-green-600">
                {formatCurrency(filteredInvoices.reduce((sum, inv) => 
                  sum + (inv.totalPaid || 0), 0
                ))}
              </div>
            </div>
            <div>
              <div className="text-xs text-gray-500 mb-1">Outstanding</div>
              <div className="text-lg font-semibold text-yellow-600">
                {formatCurrency(filteredInvoices.reduce((sum, inv) => 
                  sum + Math.max(0, (inv.totalAmount || 0) - (inv.totalPaid || 0)), 0
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Email Modal */}
      <EmailModal
        isOpen={emailModal.isOpen}
        onClose={closeEmailModal}
        document={emailModal.invoice}
        documentType="invoice"
        client={emailModal.client}
        companySettings={settings}
        onEmailSent={handleEmailSent}
      />
    </>
  );
};

export default InvoicesPage;